(() => {
  'use strict';

  /* Hoe lang na een klik op een toevoegknop we het cart-event nog als
     "toegevoegd" beschouwen. Ruim genoeg voor een trage verbinding, kort
     genoeg dat een aanpassing op de mandjepagina er niet in valt. */
  const KLIKVENSTER = 6000;

  /* --- Prijsopmaak ------------------------------------------------------ */

  /* /cart.js geeft bedragen in centen en geen opgemaakte prijs. Het formaat
     van de winkel komt uit Liquid mee als data-attribuut, bijvoorbeeld
     "€{{amount_with_comma_separator}}". Hieronder wordt dat ingevuld.

     Niet vervangen door een vaste opmaak: staat de winkel later in een
     andere munt of zonder decimalen, dan volgt dit automatisch mee. */
  function groepeer(tekst, duizend, decimaal) {
    const stukken = tekst.split('.');
    const heel = stukken[0].replace(/\B(?=(\d{3})+(?!\d))/g, duizend);
    return stukken[1] ? heel + decimaal + stukken[1] : heel;
  }

  function geld(centen, formaat) {
    const patroon = /\{\{\s*(\w+)\s*\}\}/;
    const gevonden = formaat.match(patroon);
    const bedrag = centen / 100;

    if (!gevonden) return bedrag.toFixed(2);

    let waarde;
    switch (gevonden[1]) {
      case 'amount':
        waarde = groepeer(bedrag.toFixed(2), ',', '.');
        break;
      case 'amount_no_decimals':
        waarde = groepeer(String(Math.round(bedrag)), ',', '');
        break;
      case 'amount_with_comma_separator':
        waarde = groepeer(bedrag.toFixed(2), '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        waarde = groepeer(String(Math.round(bedrag)), '.', '');
        break;
      case 'amount_with_apostrophe_separator':
        waarde = groepeer(bedrag.toFixed(2), "'", '.');
        break;
      default:
        waarde = bedrag.toFixed(2);
    }

    return formaat.replace(patroon, waarde);
  }

  /* --- Het element ------------------------------------------------------ */

  class MandjeMelding extends HTMLElement {
    connectedCallback() {
      this.gebied = this.querySelector('[data-mm-gebied]');
      this.sjabloon = this.querySelector('[data-mm-sjabloon]');
      if (!this.gebied || !this.sjabloon) return;

      this.formaat = this.dataset.mmGeld || '€{{amount_with_comma_separator}}';
      this.duur = parseInt(this.dataset.mmDuur, 10) || 5000;

      this.variantId = null;
      this.klikTijd = 0;
      this.timer = null;

      this.opKlik = this.opKlik.bind(this);
      this.opCartWijziging = this.opCartWijziging.bind(this);
      this.opToets = this.opToets.bind(this);

      /* Capture-fase: de knop van Horizon roept preventDefault en stopt
         daarna zijn eigen verwerking; in de bubble-fase zijn we soms te
         laat. In capture zijn we er altijd eerst. */
      document.addEventListener('click', this.opKlik, true);
      document.addEventListener('shopify:cart:lines-update', this.opCartWijziging);
      document.addEventListener('keydown', this.opToets);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.opKlik, true);
      document.removeEventListener('shopify:cart:lines-update', this.opCartWijziging);
      document.removeEventListener('keydown', this.opToets);
      clearTimeout(this.timer);
    }

    /* Elke toevoegknop in het thema heet name="add": de quick-add op de
       kaarten, de knop op de productpagina en die in onze artikelzijkolom.
       Eén selector dekt dus alles, ook wat er later bijkomt. */
    opKlik(e) {
      const knop = e.target.closest('[name="add"]');
      if (!knop) return;

      const formulier = knop.closest('form');
      const veld = formulier && formulier.querySelector('[name="id"]');
      this.variantId = veld ? veld.value : null;
      this.klikTijd = Date.now();
    }

    async opCartWijziging(e) {
      /* Geen recente klik op een toevoegknop: dan komt dit event van een
         aantal wijzigen of iets verwijderen, en daar hoort geen
         bevestiging bij. */
      if (Date.now() - this.klikTijd > KLIKVENSTER) return;

      /* Het event zegt zelf ook wat er gebeurde: add, update of remove.
         Een aantal wijzigen binnen die zes seconden krijgt zo ook geen
         melding. */
      if (e && e.action && e.action !== 'add') return;

      /* Gaat de drawer van Horizon ooit toch open, dan is die de
         bevestiging en houden we onze mond.

         Let op de selector. De opbouw op de winkel is:
           theme-drawer#cart-drawer > dialog[ref="panel"] > cart-drawer-component
         De open-stand zit dus op die dialog, boven cart-drawer-component en
         niet erin. Ik keek eerst naar cart-drawer-component zelf, en dat
         geeft altijd "niet open", ook als de drawer wel openstaat. */
      const paneel = document.querySelector('#cart-drawer dialog');
      if (paneel && paneel.open) return;

      /* Staat "drawer automatisch openen" aan, dan gaat hij zo meteen open,
         ook als dat pas na de server gebeurt (leeg mandje, quick-add). Het
         attribuut staat er dan al; de open-stand nog niet. */
      if (document.querySelector('cart-drawer-component[auto-open]')) return;

      /* Horizon stuurt het event uit VOORDAT het product naar de server
         gaat. Zonder te wachten op e.promise geeft /cart.js nog het mandje
         van vóór de klik: een nieuw product staat er dan niet in en je krijgt
         de verkeerde regel of geen melding. Mislukt het toevoegen, dan toont
         Horizon zelf de fout en zwijgen wij. */
      try {
        const uitkomst = await (e && e.promise);
        if (uitkomst && uitkomst.detail && uitkomst.detail.didError) return;
      } catch (fout) {
        return;
      }

      const wortel = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      let mandje;
      try {
        const antwoord = await fetch(wortel + 'cart.js', { headers: { Accept: 'application/json' } });
        if (!antwoord.ok) return;
        mandje = await antwoord.json();
      } catch (e) {
        /* Netwerk weg: dan liever geen melding dan een lege melding. */
        return;
      }

      if (!mandje || !mandje.items || !mandje.items.length) return;

      /* De regel bij het variant-id uit het formulier. Bij een product met
         formaten staat in dat veld het gekozen formaat, dus dit is de enige
         manier om de juiste regel te pakken. Lukt het niet, dan de laatste
         regel: beter een net iets andere titel dan geen bevestiging. */
      let regel = null;
      if (this.variantId) {
        regel = mandje.items.find((item) => String(item.variant_id) === String(this.variantId));
      }
      if (!regel) regel = mandje.items[mandje.items.length - 1];

      this.toon(regel);
    }

    opToets(e) {
      if (e.key === 'Escape' && this.gebied.firstElementChild) this.verberg();
    }

    toon(regel) {
      clearTimeout(this.timer);

      const kaart = this.sjabloon.content.cloneNode(true).firstElementChild;

      const beeld = kaart.querySelector('[data-mm-img]');
      if (regel.featured_image && regel.featured_image.url) {
        /* De url uit cart.js heeft al een querystring met ?v=, dus het
           formaat komt er met & achter en niet met ?. */
        beeld.src = regel.featured_image.url + '&width=160';
        beeld.alt = regel.featured_image.alt || '';
      } else {
        beeld.remove();
      }

      kaart.querySelector('[data-mm-titel]').textContent = regel.product_title || regel.title;

      /* "Default Title" is wat Shopify een variant noemt bij een product
         zonder keuzes. Dat wil niemand op zijn scherm zien. */
      const variantVak = kaart.querySelector('[data-mm-variant]');
      const variant = regel.variant_title;
      if (variant && variant !== 'Default Title') {
        variantVak.textContent = variant;
      } else {
        variantVak.remove();
      }

      /* Het aantal alleen tonen als het er meer dan één is. Naast "2 ×" hoort
         de stukprijs en niet de regelprijs: anders staat er "2 × €20,00"
         bij een regel van €20,00 in totaal. */
      kaart.querySelector('[data-mm-prijs]').textContent =
        regel.quantity > 1
          ? regel.quantity + ' × ' + geld(regel.final_price, this.formaat)
          : geld(regel.final_line_price, this.formaat);

      kaart.querySelectorAll('[data-mm-sluit]').forEach((knop) => {
        knop.addEventListener('click', () => this.verberg());
      });

      /* "Naar het mandje" opent de zijbalk. De mandjepagina gebruikt de
         winkel niet; alleen als de zijbalk ontbreekt gaan we daar toch heen. */
      const naarMandje = kaart.querySelector('[data-mm-mandje]');
      if (naarMandje) {
        naarMandje.addEventListener('click', () => {
          const lade = document.querySelector('#cart-drawer');
          this.verberg();
          if (lade && typeof lade.open === 'function') {
            lade.open();
          } else {
            window.location.href = naarMandje.dataset.mmUrl || '/cart';
          }
        });
      }

      /* De teller pauzeren zolang de muis of het toetsenbord in de kaart
         zit. Een melding die wegvalt terwijl je hem aan het lezen bent of
         net naar de knop tabt, is erger dan geen melding. */
      kaart.addEventListener('pointerenter', () => clearTimeout(this.timer));
      kaart.addEventListener('focusin', () => clearTimeout(this.timer));
      kaart.addEventListener('pointerleave', () => this.startTimer());
      kaart.addEventListener('focusout', () => this.startTimer());

      this.gebied.replaceChildren(kaart);

      /* Twee frames wachten voor de klasse die de animatie start: in
         hetzelfde frame als het invoegen berekent de browser geen
         beginstand en verschijnt de kaart zonder beweging. */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => kaart.classList.add('mm__kaart--in'));
      });

      this.startTimer();
    }

    startTimer() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.verberg(), this.duur);
    }

    verberg() {
      clearTimeout(this.timer);
      const kaart = this.gebied.firstElementChild;
      if (!kaart) return;

      kaart.classList.remove('mm__kaart--in');
      kaart.classList.add('mm__kaart--uit');

      /* Opruimen na de animatie, met een vaste tijd als vangnet. Zonder dat
         vangnet blijft de kaart in de DOM staan als de overgang niet vuurt,
         bijvoorbeeld bij prefers-reduced-motion of in een tab op de
         achtergrond. */
      let opgeruimd = false;
      const ruimOp = () => {
        if (opgeruimd) return;
        opgeruimd = true;
        if (kaart.parentNode === this.gebied) this.gebied.replaceChildren();
      };

      kaart.addEventListener('transitionend', ruimOp, { once: true });
      setTimeout(ruimOp, 400);
    }
  }

  if (!customElements.get('el-mandje-melding')) {
    customElements.define('el-mandje-melding', MandjeMelding);
  }
})();